import type { DetectionResult } from '../types/app';

const labels: Record<DetectionResult['format'], string> = {
  plain: 'Klartext',
  markdown: 'Markdown',
  html: 'HTML oder Rich Text',
  office: 'Microsoft Word oder Office',
  pdf: 'Text aus einer PDF',
};

export function detectSourceFormat(plainText: string, rawHtml = ''): DetectionResult {
  const html = rawHtml.trim();
  const text = plainText.replace(/\r\n?/g, '\n').trim();
  const reasons: string[] = [];

  if (/class=["'][^"']*Mso|mso-[a-z-]+\s*:|urn:schemas-microsoft-com|<o:p\b/i.test(html)) {
    reasons.push('Office-spezifische HTML-Merkmale gefunden');
    return { format: 'office', label: labels.office, reasons };
  }

  const semanticTags = html.match(/<(?:h[1-6]|p|ul|ol|li|table|blockquote|strong|em|a)\b/gi)?.length ?? 0;
  if (semanticTags > 0 || /<!doctype\s+html|<html\b|<body\b/i.test(text)) {
    reasons.push('formatierende HTML-Elemente gefunden');
    return { format: 'html', label: labels.html, reasons };
  }

  const markdownSignals = [
    /^#{1,6}\s+\S+/m,
    /^\s*(?:[-*+] |\d+\. )\S+/m,
    /\*\*[^*\n]+\*\*|__[^_\n]+__/,
    /\[[^\]]+\]\((?:https?:\/\/|\/)[^)]+\)/,
    /^```[\s\S]*?^```/m,
    /^>\s+\S+/m,
  ].filter((pattern) => pattern.test(text)).length;
  if (markdownSignals >= 2 || (markdownSignals === 1 && /^#{1,6}\s+/m.test(text))) {
    reasons.push(`${markdownSignals} typische Markdown-Merkmale gefunden`);
    return { format: 'markdown', label: labels.markdown, reasons };
  }

  const lines = text.split('\n').filter((line) => line.trim());
  if (lines.length >= 4) {
    const shortHardLines = lines.filter((line) => line.length >= 25 && line.length <= 100).length;
    const nonTerminal = lines.slice(0, -1).filter((line) => !/[.!?;:]\s*$/.test(line)).length;
    const blankRatio = (text.match(/\n\s*\n/g)?.length ?? 0) / lines.length;
    if (shortHardLines / lines.length >= 0.6 && nonTerminal / Math.max(1, lines.length - 1) >= 0.5 && blankRatio < 0.2) {
      reasons.push('viele ähnlich lange harte Zeilen ohne Absatzabstände gefunden');
      return { format: 'pdf', label: labels.pdf, reasons };
    }
  }

  reasons.push('keine eindeutigen Formatmerkmale gefunden');
  return { format: 'plain', label: labels.plain, reasons };
}
